import * as turf from '@turf/turf'
import Bounds from '@/model/geo/Bounds'

const NULL_BBOX = [Infinity, Infinity, -Infinity, -Infinity]

/**
 * bboxをBoundsに変換する
 * @param {Array.<Number>} bbox(turf.bboxの戻値)
 * @returns {Bounds} bounds
 */
const bboxToBounds = (bbox) => {
  if (
    !Array.isArray(bbox) ||
    bbox.every((value, index) => value === NULL_BBOX[index])
  ) {
    return new Bounds({
      sw: [0, 0],
      ne: [0, 0],
    })
  }
  return new Bounds({
    sw: [bbox[0], bbox[1]],
    ne: [bbox[2], bbox[3]],
  })
}

/**
 * ジオメトリ処理ユーティリティークラス
 */
class GeometryUtil {
  /**
   * 重心座標を求める
   *
   * @param {Object} geometry ジオメトリ
   * @returns {Array.<Number>} 重心座標(x,y)
   */
  centroid(geometry) {
    return turf.getCoord(turf.centroid(geometry))
  }

  /**
   * バッファジオメトリを求める
   * 内部で turf.buffer を使用
   * @param {Object} geometry ジオメトリ
   * @param {Number} radius 半径(単位はoptions.unitsで指定)
   * @param {Object} [options={ units: 'meters', steps: 8 }] turf.bufferに渡すオプション
   */
  buffer(geometry, radius, options = { units: 'meters', steps: 8 }) {
    return turf.getGeom(turf.buffer(geometry, radius, options))
  }

  /**
   * 最北点を取得する
   * @param {Object} geometry ジオメトリ
   * @returns {Array.<Number>} 最北点座標
   */
  getNorthEnd(geometry) {
    const { type, coordinates } = geometry
    const reducer = (coords) =>
      coords.reduce((prev, current) => (prev[1] < current[1] ? current : prev))

    switch (type) {
      case 'Point':
        return coordinates
      case 'MultiPoint':
        return reducer(coordinates)
      case 'LineString':
        return reducer(coordinates)
      case 'Polygon':
        return reducer(coordinates[0])
      case 'MultiLineString':
        return reducer(coordinates.map((coords) => reducer(coords)))
      case 'MultiPolygon':
        return reducer(coordinates.map((coords) => reducer(coords[0])))
      default:
        return []
    }
  }

  /**
   * ジオメトリのboundsを取得する
   * @param {Object|Array.<Object>} geometry ジオメトリ
   * @returns {Bounds} bounds(geometryが配列の場合は全てを含むbounds)
   */
  getBounds(geometry) {
    if (!Array.isArray(geometry)) {
      return bboxToBounds(turf.bbox(geometry))
    }
    return geometry
      .map((geom) => bboxToBounds(turf.bbox(geom)))
      .reduce((accum, current) => {
        if (accum.isEmpty) return current
        if (current.isEmpty) return accum
        return accum.union(current)
      }, new Bounds())
  }
}

export default new GeometryUtil()
